import React, { useContext, useMemo } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { IconType } from 'react-icons';
import {
  FiUser,
  FiLock,
  FiUsers,
  FiMail,
  FiActivity,
  FiClock,
  FiCheckCircle,
  FiCalendar,
  FiBell,
} from 'react-icons/fi';
import { selectUser } from '../../entities/auth/model';
import { AbilityContext } from '@shared/lib/ability';

// Иконки пунктов сайдбара. Подбирали под лучайд-стиль из макета:
// outline, ровные штрихи. Если для пункта иконки нет — fallback FiUser.
const ICONS: Record<string, IconType> = {
  profile: FiUser,
  security: FiLock,
  users: FiUsers,
  email: FiMail,
  routing: FiActivity,
  sla: FiClock,
  statuses: FiCheckCircle,
  schedules: FiCalendar,
  notifications: FiBell,
  'email-channel': FiMail,
};

// ── scope для раздела «Пользователи и роли» ──
// Повторяет серверный _get_scope в views/roles.py:
//   - 'admin'   — is_superuser или группа admins
//   - 'company' — COMPANY_OWNER + привязанная company
//   - 'none'    — остальные
const OWNER_GROUP_NAMES = ['COMPANY_OWNER'];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function resolveUsersScope(me: any): 'admin' | 'company' | 'none' {
  if (!me) return 'none';
  if (me.is_superuser) return 'admin';
  const groups: string[] = me.groups || [];
  if (groups.includes('admins')) return 'admin';
  if (me.company && groups.some((g) => OWNER_GROUP_NAMES.includes(g))) {
    return 'company';
  }
  return 'none';
}

interface NavItem {
  label: string;
  path: string;
  icon: string;
  hidden?: boolean;
}

interface NavGroup {
  group: string;
  items: NavItem[];
}

const Settings: React.FC = () => {
  const location = useLocation();
  const me = useSelector(selectUser);
  const ability = useContext(AbilityContext);

  const navigation = useMemo<NavGroup[]>(() => {
    const usersScope = resolveUsersScope(me);
    const canSeeUsersRoles = usersScope !== 'none';
    const canManageHd = ability.can('hd_manage_settings', 'ticket');
    // Project-пункты вроде email-уведомлений — для админов FMeter и
    // OWNER'ов компании (пока что — можно расширить позже).
    const canSeeProjectSettings = canSeeUsersRoles;

    return [
      {
        group: 'АККАУНТ',
        items: [
          { label: 'Профиль', path: '/settings/profile', icon: 'profile' },
          { label: 'Безопасность', path: '/settings/security', icon: 'security' },
        ],
      },
      {
        group: 'ПРОЕКТ',
        items: [
          {
            label: 'Пользователи и роли',
            path: '/settings/users',
            icon: 'users',
            hidden: !canSeeUsersRoles,
          },
          {
            label: 'Email-уведомления',
            path: '/settings/email-notifications',
            icon: 'email',
            hidden: !canSeeProjectSettings,
          },
        ],
      },
      {
        group: 'HELPDESK',
        items: [
          { label: 'Маршрутизация', path: '/settings/helpdesk/routing', icon: 'routing', hidden: !canManageHd },
          { label: 'SLA-политики', path: '/settings/helpdesk/sla', icon: 'sla', hidden: !canManageHd },
          { label: 'Статусы и закрытые', path: '/settings/helpdesk/statuses', icon: 'statuses', hidden: !canManageHd },
          { label: 'Расписания', path: '/settings/helpdesk/schedules', icon: 'schedules', hidden: !canManageHd },
          { label: 'Уведомления', path: '/settings/helpdesk/notifications', icon: 'notifications', hidden: !canManageHd },
          { label: 'Email-канал', path: '/settings/helpdesk/email-channel', icon: 'email-channel', hidden: !canManageHd },
        ],
      },
    ];
  }, [me, ability]);

  return (
    // -ml-4 компенсирует p-4 контейнера Area: внутренний сайдбар
    // должен прилипать к правому краю основного сайдбара без зазора.
    <div className="flex gap-6 min-h-[calc(100vh-80px)] -ml-4">
      {/* Внутренний сайдбар настроек.
          - bg-white: фон по макету (как карточка справа от глобального меню).
          - border-l: тонкий разделитель между основным и внутренним сайдбаром.
          - -mt-4: компенсирует mb-4 хедера, чтобы белый сайдбар прижимался
            прямо к нижней границе шапки без серого зазора.
          - relative: чтобы красная полоса активного пункта прижалась к правому краю.
          - gap-6 на родителе создаёт промежуток между сайдбаром и основной областью. */}
      <aside className="relative w-[260px] min-w-[260px] bg-white border-l border-[#e5e7eb] -mt-4 pt-4 pb-4">
        {navigation.map((group) => {
          const visibleItems = group.items.filter((item) => !item.hidden);
          if (visibleItems.length === 0) return null;
          return (
            <div key={group.group} className="mb-6">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-[0.08em] mb-2 px-5">
                {group.group}
              </p>
              <nav className="flex flex-col gap-0.5">
                {visibleItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  const Icon = ICONS[item.icon] ?? FiUser;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={`group relative flex items-center gap-3 px-5 py-2.5 text-[15px] transition-colors ${
                        isActive
                          ? 'text-[#2d2c2c] font-semibold'
                          : 'text-[#7c8089] font-normal hover:text-[#2d2c2c]'
                      }`}
                    >
                      <Icon
                        className={`shrink-0 text-[18px] transition-colors ${
                          isActive
                            ? 'text-[#e3282a]'
                            : 'text-[#9aa0a8] group-hover:text-[#2d2c2c]'
                        }`}
                      />
                      <span className="truncate">{item.label}</span>

                      {/* Красная полоса-индикатор активного пункта.
                          Прижата к правому краю белого сайдбара. */}
                      {isActive && (
                        <span
                          aria-hidden="true"
                          className="absolute top-1 bottom-1 right-0 w-[3px] rounded-l bg-[#e3282a]"
                        />
                      )}
                    </NavLink>
                  );
                })}
              </nav>
            </div>
          );
        })}
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
};

export default Settings;
